<template>
  <div>
    <v-fade-transition>
      <v-btn
        v-if="is_chart_modified"
        class="reset-button"
        color="vtblue"
        large
        dark
        fixed
        bottom
        right
        @click="resetChartZoom"
      >
        {{ $t("common.labels.resetView") }}
        <v-icon right dark> mdi-restart </v-icon>
      </v-btn>
    </v-fade-transition>
    <div class="chart-container" v-for="(chart, index) in data" :key="`${index}_container`">
      <div :class="{ 'hide-container': !chart || chart.length == 0 }">
        <div class="mt-6 mb-4 primaryOutline--text text-subtitle-2">{{ config[index].label }}</div>
        <div :id="`${config[index].id}_3`" class="fill"></div>
        <div class="legends">
          <v-row no-gutters>
            <v-col
              cols="2"
              v-for="(d, idx) in chart"
              :key="`${config[index].id}_${idx}_legends`"
              @click="legendClicked($event, index, idx)"
            >
              <div class="d-flex legend text-caption" :style="{ color: config[index].colors[idx] }">
                <div :style="{ background: config[index].colors[idx] }" class="legend-color mr-1"></div>
                <div>
                  {{ d.label }}
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { LightningChart } from "../services/lightningchart";
import {
  AxisTickStrategies,
  AxisScrollStrategies,
  synchronizeAxisIntervals,
  emptyLine,
  transparentFill,
  emptyFill,
  SolidFill,
  SolidLine,
  ColorRGBA,
  ColorHEX,
  FontSettings,
} from "@arction/lcjs";
import { makeFlatTheme } from "@arction/lcjs-themes";

export default {
  name: "LineRangeLightningChart",
  props: {
    data: Array,
    config: Array,
    in_sync: Boolean,
    duration: Number,
    period: Object,
  },
  watch: {
    in_sync: function (newVal) {
      this.synchronizeCharts(newVal);
    },
    "$vuetify.theme.dark": {
      handler() {
        this.chart.map((c) => c.dispose());
        this.chart = [];
        this.initialize();
      },
    },
  },
  data: function () {
    return {
      chart: [],
      lc: null,
      axisX: [],
      chartId: "test-lightning",
      syncHandle: null,
      lineSeries: [],
      show_days: null,
      is_chart_modified: false,
    };
  },
  methods: {
    initialize: function () {
      const self = this;
      const myCustomTheme = makeFlatTheme({
        fontFamily: "Roboto, sans-serif",
        backgroundColor: this.$vuetify.theme.dark ? ColorHEX("#1c1d21") : ColorHEX("#ffffff"),
        textColor: this.$vuetify.theme.dark ? ColorHEX("#ffffffaa") : ColorHEX("1c1d2188"),
        dataColors: [ColorHEX("#ffff5b"), ColorHEX("#ffcd5b"), ColorHEX("#ff9b5b")],
        axisColor: this.$vuetify.theme.dark ? ColorHEX("#00000000") : ColorHEX("1c1d2188"),
        gridLineColor: this.$vuetify.theme.dark ? ColorHEX("#303030ff") : ColorHEX("1c1d2122"),
        uiBackgroundColor: this.$vuetify.theme.dark ? ColorHEX("#303030ff") : ColorHEX("ffffff"),
        uiBorderColor: this.$vuetify.theme.dark ? ColorHEX("#303030ff") : ColorHEX("ffffff"),
        dashboardSplitterColor: ColorHEX("#552d2d66"),
      });

      this.lc = LightningChart.initialize();
      // const dateOrigin = new Date()
      this.data.map((d, index) => {
        this.lineSeries[index] = [];
        let chart = this.lc.ChartXY({
          overrideInteractionMouseButtons: {
            chartXYPanMouseButton: 0, // LMB
            chartXYRectangleZoomFitMouseButton: 2, // RMB
          },
          container: `${this.config[index].id}_3`,
          theme: myCustomTheme,
          defaultAxisX: {
            type: "linear-highPrecision",
          },
          // defaultAxisXTickStrategy: AxisTickStrategies.DateTime
          // defaultAxisXTickStrategy: AxisTickStrategies.DateTime(dateOrigin)
          // , (tickStrategy) => tickStrategy.setDateOrigin(dateOrigin)
        });
        let axisX = chart
          .getDefaultAxisX()
          .setTickStrategy(AxisTickStrategies.DateTime, (tickStrategy) =>
            tickStrategy.setMajorTickStyle((tickStyle) => tickStyle.setLabelFont((font) => font.setSize(12)))
          )
          .setInterval({
            start: this.time_range[0],
            end: this.time_range[1],
          })
          .setTitle(this.config[index].xAxis)
          .setTitleFont((font) => font.setSize(12));
        chart
          .getDefaultAxisY()
          .setTitle(this.config[index].yAxis)
          .setTitleFont((font) => font.setSize(12))
          .setChartInteractionPanByDrag(false);

        const axisYLine =
          this.config[index].yLineAxis || this.config[index].yBarAxis
            ? chart
                .addAxisY({ opposite: true })
                .setTitle(this.config[index].yLineAxis || this.config[index].yBarAxis)
                .setTitleFont((font) => font.setSize(12))
                .setChartInteractionPanByDrag(false)
            : null;

        // Set chart title
        chart.setTitle("");
        chart.setAnimationsEnabled(false);

        chart.setMouseInteractionWheelZoom(false);
        axisX
          // Configure scrolling time axis
          .setScrollStrategy(AxisScrollStrategies.progressive)
          // Disable default interactions on X axis
          .setAxisInteractionZoomByWheeling(false)
          .setAxisInteractionPanByDragging(false)
          .setNibInteractionScaleByWheeling(false)
          .setNibInteractionScaleByDragging(false);

        const handleMouseWheel = (_, event) => {
          this.is_chart_modified = true;
          // NOTE: This is a convenience method. Alternatively you can use `Axis.setInterval` for full control.
          axisX.zoom(axisX.getInterval().end, Math.sign(event.deltaY));
          event.preventDefault();
          event.stopPropagation();
        };

        d &&
          d.length > 0 &&
          d.map((data, idx) => {
            const areaRange = chart.addAreaRangeSeries({
              xAxis: chart.getDefaultAxisX(),
              yAxis: data.type === "bar" && axisYLine && idx != 0 ? axisYLine : chart.getDefaultAxisY(),
            });

            areaRange
              .setLowFillStyle(
                new SolidFill({
                  color: ColorHEX(this.config[index].areaColors[idx]).setA(100),
                })
              )
              .setHighFillStyle(
                new SolidFill({
                  color: ColorHEX(this.config[index].areaColors[idx]).setA(100),
                })
              )
              .setLowStrokeStyle(
                new SolidLine({
                  thickness: 1,
                  fillStyle: new SolidFill({
                    color: ColorHEX(this.config[index].areaColors[idx]),
                  }),
                })
              )
              .setHighStrokeStyle(
                new SolidLine({
                  thickness: 1,
                  fillStyle: new SolidFill({
                    color: ColorHEX(this.config[index].areaColors[idx]),
                  }),
                })
              )
              .setCursorEnabled(false)
              .setName(data.label || data.id);
            data.range.map((point) => {
              areaRange.add({ position: point.x, high: point.yMax, low: point.yMin });
            });
            const lineSeries = chart.addLineSeries({
              // Optimize line series for progressively increasing X coordinates.
              // dataPattern: { pattern: 'ProgressiveX', regularProgressiveStep: true, allowDataGrouping: true },
              xAxis: chart.getDefaultAxisX(),
              yAxis: data.type === "bar" && axisYLine && idx != 0 ? axisYLine : chart.getDefaultAxisY(),
            });
            // Add data points to the line series
            lineSeries
              .add(data.values)
              .setStrokeStyle(
                new SolidLine({
                  thickness: 1,
                  fillStyle: new SolidFill({
                    color: ColorHEX(this.config[index].colors[idx]),
                  }),
                })
              )
              .setName(data.label || data.id)
              .onMouseWheel(handleMouseWheel);
            lineSeries.setCursorEnabled(false);
            this.lineSeries[index][idx] = [lineSeries, areaRange, true];
          });

        // Set stroke style of the line
        // lineSeries.setStrokeStyle((style) => style.setThickness(5))
        // Trigger custom event handler when mouse wheeling over chart, X axis or even series.
        chart.onSeriesBackgroundMouseWheel(handleMouseWheel);
        axisX.onAxisInteractionAreaMouseWheel(handleMouseWheel);
        this.chart.push(chart);
        this.axisX.push(axisX);
        this.setSynchronousHover(chart, index, self);
      });
      this.syncHandle = this.synchronizeCharts(true);
      // lineSeries.onMouseWheel(handleMouseWheel)
      // this.chart.onSeriesBackgroundMouseDrag((chart, event, button, startLocation, delta) => {
      //   // ... implement custom logic that replaces built-in rectangle zoom interaction
      //   console.log(startLocation, delta);
      // })
    },
    resetChartZoom() {
      // this function should reset the zoom and pan of the chart and revert to the original view
      this.chart.map((c) => {
        c.getDefaultAxisX().fit(false);
        c.getDefaultAxisY().fit(false);
      });
      this.is_chart_modified = false;
    },
    setSynchronousHover(chart, index, self) {
      // Create custom cursor UI by adding HTML elements to the document.
      const styleElem = document.head.appendChild(document.createElement("style"));
      const textBox = document.createElement("div");
      textBox.id = "resultTable";
      const line = document.createElement("div");
      line.id = "line";
      const line2 = document.createElement("div");
      line2.id = "line2";
      const arrow = document.createElement("div");
      arrow.id = "arrow";
      textBox.appendChild(line);
      textBox.appendChild(line2);
      textBox.appendChild(arrow);
      chart.engine.container.append(textBox);

      // Implement custom cursor logic with events.
      const showCursorAt = (clientCoordinate) => {
        // Translate mouse location to Axis.
        const mouseLocationAxis = chart.translateCoordinate(clientCoordinate, chart.coordsAxis);

        // Solve nearest data point to the mouse on each series.
        const nearestDataPoints = self.lineSeries[index].map((el) => el[0].solveNearestFromScreen(clientCoordinate));
        const nearestDataPointsAreaRange = self.lineSeries[index].map((el) =>
          el[1].solveNearestFromScreen(clientCoordinate)
        );

        // Find the nearest solved data point to the mouse.
        const nearestPoint =
          nearestDataPoints && nearestDataPoints.length > 0
            ? nearestDataPoints.reduce((prev, curr, i) => {
                if (!prev) return curr;
                if (!curr) return prev;
                return Math.abs(mouseLocationAxis.y - curr.location.y) < Math.abs(mouseLocationAxis.y - prev.location.y)
                  ? curr
                  : prev;
              })
            : null;

        if (nearestPoint && nearestPoint.location && nearestPoint.location.x) {
          // Translate data point from Axis to client.
          const nearestPointClient = chart.translateCoordinate(
            nearestPoint.location,
            chart.coordsAxis,
            chart.coordsClient
          );

          // Position and format custom HTML cursor.
          if (nearestPoint.location.x > chart.getDefaultAxisX().getInterval().end / 1.5) {
            textBox.style.left = `${Math.round(nearestPointClient.clientX - textBox.clientWidth - 10)}px`;
            textBox.style.top = `${Math.round(nearestPointClient.clientY - textBox.clientHeight / 2)}px`;
            arrow.style.left = "200px";
            arrow.style.transform = "translate(-40%, -50%) rotate(-135deg)";
          } else {
            textBox.style.left = `${Math.round(nearestPointClient.clientX + 10)}px`;
            textBox.style.top = `${Math.round(nearestPointClient.clientY - textBox.clientHeight / 2)}px`;
            arrow.style.left = "-1px";
            arrow.style.transform = "translate(-50%, -50%) rotate(45deg)";
          }

          const point = nearestDataPoints.map((el) => (el ? el.location.y : 0));
          let rowElement = "";
          for (let i = 0; i < self.lineSeries[index].length; i++) {
            line.innerHTML = `<b>${chart.getDefaultAxisX().formatValue(nearestDataPoints[i].location.x)}
              </b>`;
            const sign = point[i] > 0 ? "+" : "";
            rowElement += `<div class='values' style='color: ${
              this.config[index].colors[i]
            }' ><span class="borderLight--text">${self.lineSeries[index][i][0].getName()} Avg.:</span> <b>${
              sign + chart.getDefaultAxisY().formatValue(nearestDataPoints[i].location.y)
            } ${this.data[index][i].suffix}</b>
                </div>`;
            rowElement += `<div class='values' style='color: ${this.config[index].colors[i]}' ><span class="borderLight--text">Range :</span> <b>${nearestDataPointsAreaRange[i].resultTableContent[2][2]} - ${nearestDataPointsAreaRange[i].resultTableContent[3][2]}</b>
                </div>`;
          }
          line2.innerHTML = rowElement;

          textBox.style.opacity = 1;
        } else {
          textBox.style.opacity = 0;
        }
      };

      const mouseMoveHandler = (_, event) => {
        showCursorAt(event);
      };
      self.lineSeries[index].forEach((series) => {
        series[0].onMouseMove(mouseMoveHandler);
        series[1].onMouseMove(mouseMoveHandler);
      });
      chart.onSeriesBackgroundMouseMove(mouseMoveHandler);
      chart.onSeriesBackgroundMouseLeave((_, event) => {
        textBox.style.opacity = 0;
      });
      chart.onSeriesBackgroundMouseDragStart((_, e) => {
        this.is_chart_modified = true;
        textBox.style.opacity = 0;
      });

      chart.getDefaultAxisY().onIntervalChange(() => {
        textBox.style.opacity = 0;
      });
      chart.getDefaultAxisX().onIntervalChange(() => {
        textBox.style.opacity = 0;
      });

      // Add CSS.
      function addStyle(styleString) {
        const style = document.createElement("style");
        style.textContent = styleString;
        document.head.append(style);
      }

      addStyle(`
    #resultTable {
        background-color: var(--v-backgroundWhite-base);
        color: var(--v-borderLight-base);
        font-size: 11px;
        border: solid var(--v-borderLight-base) 1px;
        border-radius: 5px;
        width: 200px;
        // height: 110px;
        height: auto;
        top: 0;
        left: 0;
        position: fixed;
        padding: 0;
        pointer-events:none;
        z-index: 1;
        transition: left 0.2s, top 0.2s, opacity 0.2s;
        opacity: 0.0;
    }
    #arrow {
        position: absolute;
        background-color: var(--v-backgroundLight-base);
        height: 10px;
        width: 10px;
        top: 49%;
        border-left: solid var(--v-borderLight-base) 1px;
        border-bottom: solid var(--v-borderLight-base) 1px;
        box-shadow: 0 0 10px rgba(0, 0, 0, .5);
        transform-origin: center center;
    }
    p {
        margin:0;
    }
    #date {
        text-align: center;
    }
    .values {
        display: flex;
        justify-content: space-between;
        align-items center;
        margin: 5px 0;
    }
    .values span {
        margin-right: 10px 
        font-size: 10px;
    }
    #line {
        background-color: var(--v-backgroundDark-base);
        border-top-right-radius: 4px;
        border-top-left-radius: 4px;
        margin: 0;
        padding: 7px;
        font-size: 11px;
        height: 20%;
        font-family: Roboto, sans-serif;
        width: auto;
        heigth: 100%;
    }
    #line2 {
        font-size: 11px;
        font-weight: 900;
        padding: 2px 10px
    }
`);
    },
    legendClicked: function (event, chart_id, line_id) {
      event.target.parentElement.classList.toggle("disable");
      this.lineSeries.forEach((e, i) => {
        if (i == chart_id) {
          e.forEach((e1, i1) => {
            if (i1 == line_id) {
              e1[2] = !e1[2]; //Update series visibility param
              e1[0].setVisible(e1[2]);
              e1[1].setVisible(e1[2]);
            }
          });
        }
      });
    },
    synchronizeCharts: function (enable) {
      if (enable) return synchronizeAxisIntervals.apply(null, this.axisX);
      else this.syncHandle.remove();
      return null;
    },
  },
  created() {},
  mounted() {
    setTimeout(() => {
      this.initialize();
    }, 1500);
    //this.initialize();
  },
  computed: {
    time_range: function () {
      let today = new Date().getTime();
      return this.period
        ? [this.period.start_date, this.period.end_date]
        : [today - Math.ceil(this.duration / 4) * 86400000, today];
    },
  },
  beforeDestroy() {
    // "dispose" should be called when the component is unmounted to free all the resources used by the chart
    this.chart.map((chart) => chart.dispose());
  },
};
</script>
<style lang="scss" scoped>
.chart-container {
  margin: 0 1rem;
}

.fill {
  height: 280px;
  margin: 0 1rem;
}

.legends {
  margin: 0 1rem 2rem;

  .legend {
    cursor: pointer;

    .legend-color {
      border-radius: 50%;
      width: 16px;
      height: 16px;
    }
  }

  .legend.disable {
    color: grey !important;

    .legend-color {
      background-color: grey !important;
    }
  }
}
.hide-container {
  display: none;
}
</style>
